import { useUsercentricsOverlay } from '~/composables/useUsercentricsOverlay'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && el.tabIndex >= 0)
}

/**
 * Trap Tab focus inside `container` while active. Restores focus to `previousActiveElement` on release.
 * Yields Tab handling while Usercentrics CMP UI is open (their own focus trap takes priority).
 */
export const useFocusTrap = (container: Ref<HTMLElement | null | undefined>) => {
  let previousActiveElement: HTMLElement | null = null
  let isActive = false

  const { isBlockingFocusTraps } = useUsercentricsOverlay()

  const onKeydown = (event: KeyboardEvent): void => {
    if (!isActive || event.key !== 'Tab' || !container.value) {
      return
    }

    // Let Usercentrics keep Tab while its CMP overlay is visible
    if (isBlockingFocusTraps()) {
      return
    }

    const focusable = getFocusableElements(container.value)
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement as HTMLElement | null

    if (event.shiftKey) {
      if (active === first || !container.value.contains(active)) {
        event.preventDefault()
        last.focus()
      }
    }
    else if (active === last || !container.value.contains(active)) {
      event.preventDefault()
      first.focus()
    }
  }

  const activate = (options?: { initialFocus?: HTMLElement | null }): void => {
    if (!import.meta.client || !container.value) {
      return
    }

    previousActiveElement = document.activeElement as HTMLElement | null
    isActive = true
    document.addEventListener('keydown', onKeydown)

    // Do not steal focus from Usercentrics CMP
    if (isBlockingFocusTraps()) {
      return
    }

    const initial = options?.initialFocus
      ?? getFocusableElements(container.value)[0]
      ?? container.value

    nextTick(() => {
      if (isBlockingFocusTraps()) {
        return
      }
      initial?.focus?.()
    })
  }

  const deactivate = (): void => {
    if (!isActive) {
      return
    }

    isActive = false
    document.removeEventListener('keydown', onKeydown)

    if (previousActiveElement?.focus) {
      previousActiveElement.focus()
    }
    previousActiveElement = null
  }

  onBeforeUnmount(() => {
    deactivate()
  })

  return {
    activate,
    deactivate,
    getFocusableElements: () => (container.value ? getFocusableElements(container.value) : [])
  }
}

export default useFocusTrap
